import { AfterViewInit, Component, EventEmitter, Inject, Output, OnDestroy,
         ElementRef, ViewChild, afterNextRender, ChangeDetectorRef,
       } from '@angular/core';

import { TemplateConfig, TemplateGroupConfig
       } from 'app/collage-designer/collage-designer-config';
import { PlaceholderService
       } from 'app/collage-designer/stage/placeholder/placeholder.service';
import { MissingStructureError, PrnglService, RenderRequest
       } from 'app/core/prngl/prngl.service';
import { GalleryService } from 'app/gallery/gallery.service';
import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { DesignState } from 'collage-designer/stage/stage-config';
import { StageService } from 'collage-designer/stage/stage.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber } from 'core/utils/async-event-emitter';
import { Subscription } from 'rxjs';

interface TemplateSelection
{
  templateId: string;
  templateGroupId: string;
}

enum PlaceholderFillStrategy {
  GALLERY_IMAGES,
  MOTIVE_STRUCTURE,
}

interface PreviewOverrideConfig {
  part: string;
  strategy: PlaceholderFillStrategy;
  previewType: string;
}

const PREVIEW_OVERRIDE_CONFIGS: Array<PreviewOverrideConfig> =
[
  // NOTE: Not used right now. Enable with #31565.
  // {
  //   part: "motive",
  //   strategy: PlaceholderFillStrategy.GALLERY_IMAGES,
  //   previewType: "pieces",
  // },
  {
    part: "box",
    strategy: PlaceholderFillStrategy.MOTIVE_STRUCTURE,
    previewType: "box",
  },
];

@Component({
    selector: 'template-chooser',
    templateUrl: './template-chooser.component.html',
    styleUrls: ['./template-chooser.component.scss'],
    standalone: false
})
export class TemplateChooserComponent implements AfterViewInit, OnDestroy
{
  private subscriptions: Subscription = new Subscription();
  private stageConfigAppliedSubscription:
      AsyncEventEmitterSubscriber<boolean>;

  private static readonly LOADING_IMAGE: string =
      Utils.url("./assets/img/loading.gif");

  public trackById: (index: number, item: any) => any = Utils.trackById;
  public loadingImageStyle: any = {
    'background-image':
        `url("${TemplateChooserComponent.LOADING_IMAGE}")`,
  };
  public templateGroups: Array<TemplateGroupConfig> = [];
  public viewMode: string = "grid";
  public selectedTemplateGroupId: string|undefined = undefined;
  public selectedTemplateId: string = '';

  @Output() public selectedTemplateChange: EventEmitter<TemplateSelection> =
      new EventEmitter();

  private dragStartX: number = 0;
  private dragStartScroll: number = 0;

  private templatePreviews: {[key: string]: TemplatePreview};
  private previewImageResizeObserver?: ResizeObserver;
  private currentPreviewImageLongSide?: number;

  public constructor(
      private element: ElementRef,
      private changeDetector: ChangeDetectorRef,
      @Inject(DesignStepService) private designStepService,
      @Inject(PlaceholderService)
          private placeholderService: PlaceholderService,
      @Inject(ProductService) private productService: ProductService,
      @Inject(EventTrackingService) private eventTrackingService:
          EventTrackingService,
      @Inject(GalleryService) private galleryService: GalleryService,
      @Inject(PrnglService) private prnglService: PrnglService,
      @Inject(StageService) private stageService: StageService
  )
  {
    this.templatePreviews = {};

    this.subscriptions.add(
        galleryService.imageStockChanged.subscribe(() => {
          this.updateTemplateOverrides();
        }));

    this.subscriptions.add(
        designStepService.designStepChanged.subscribe(() => {
          this.updateTemplateOverrides();
        }));

    this.stageConfigAppliedSubscription =
        this.stageService.stageConfigApplied.subscribe(async () => {
          this.updateTemplateOverrides();
        });
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();

    this.stageService.stageConfigApplied.unsubscribe(
        this.stageConfigAppliedSubscription);
  }

  public get templateCount(): number
  {
    return this.templateGroups
      .map(g => g.templatesCount)
      .reduce((a, b) => a + b, 0);
  }

  public switchView(mode: string): void
  {
    this.viewMode = mode;
  }

  public async ngAfterViewInit(): Promise<void> {
    this.templateGroups = await this.designStepService.getTemplateGroups();
    this.selectedTemplateGroupId =
        await this.designStepService.getTemplateGroupId();
    this.selectedTemplateId = this.designStepService.getTemplateId();

    this.updateTemplateOverrides();
  }

  public getTemplatePreviews(): Array<TemplatePreview>
  {
    const group = Utils.find(
      this.templateGroups, g => g.id === this.selectedTemplateGroupId);
    if (!group) {
      return [];
    }
    const _t = group.templates;
    const templates = Object.keys(_t).map(key => _t[key]);
    let previews = [];

    for (const template of templates) {
      let preview = this.templatePreviews[template.id] || {
        template: template,
        renderRequest: undefined,
        previewUrl: template.previewUrl,
      };

      previews.push(preview);
    }

    return previews;
  }

  public async updateSelectedTemplate(templateId: string):
      Promise<void>
  {
    this.placeholderService.resetPendingPlaceholders();

    this.selectedTemplateChange.emit({
      templateId: templateId,
      templateGroupId: this.selectedTemplateGroupId,
    });

    this.eventTrackingService.track({
      category: this.productService.getCurrentProduct().id,
      action: 'template_change',
      label: templateId,
    });

    this.selectedTemplateId = templateId;
  }

  public async templateGroupClicked(templateGroupId: string): Promise<void>
  {
    this.eventTrackingService.track({
      category: this.productService.getCurrentProduct().id,
      action: 'template_group_selected',
      label: templateGroupId,
    });
    this.selectedTemplateGroupId = templateGroupId;
  }

  public onPreviewImageError(preview: TemplatePreview): void
  {
    preview.renderRequest = undefined;
    this.updateTemplatePreview(preview);
  }

  public placeholdersCount(template: TemplateConfig): number
  {
    const emptyCount = { portrait: 0, landscape: 0 };
    const count = template.imagePlaceholders || emptyCount;
    return count.portrait + count.landscape;
  }

  public groupSwitchPointerDown(event: PointerEvent): void
  {
    if (event.pointerType !== 'mouse') {
      return;
    }
    this.dragStartX = event.clientX;
    this.dragStartScroll = (event.currentTarget as HTMLElement).scrollLeft;
  }

  public groupSwitchPointerMove(event: PointerEvent): void
  {
    if (event.pointerType !== 'mouse' || event.buttons !== 1) {
      return;
    }
    const el = event.currentTarget as HTMLElement;
    const walk = event.clientX - this.dragStartX;
    if (Math.abs(walk) > 3 && !el.hasPointerCapture(event.pointerId)) {
      el.setPointerCapture(event.pointerId);
    }
    el.scrollLeft = this.dragStartScroll - walk;
  }

  public groupSwitchWheel(event: WheelEvent): void
  {
    const el = event.currentTarget as HTMLElement;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) {
      return;
    }
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      return;
    }
    const delta = event.deltaMode === WheelEvent.DOM_DELTA_PIXEL
        ? event.deltaY
        : event.deltaY * 16;
    const next = Math.min(Math.max(el.scrollLeft + delta, 0), maxScroll);
    if (next === el.scrollLeft) {
      return;
    }
    el.scrollLeft = next;
    event.preventDefault();
  }

  private updateTemplateOverrides(): void
  {
    const designState = this.designStepService.getCurrentDesignState();
    const productId = this.designStepService.getProductId();

    const part = this.designStepService.getDesignablePart(designState);
    if (!part) {
      return;
    }

    const config = PREVIEW_OVERRIDE_CONFIGS.find((c) => c.part === part.id);
    if (!config) {
      return;
    }

    const templates =
      part.templateGroups.flatMap(g => Object.values(g.templates));

    for (const template of templates.filter((t) => !!t.stageConfig)) {
      let req = this.prnglService.makeRenderRequest(
        productId, config.previewType);

      req.fillDesignedParts();

      try {
        switch (config.strategy) {
          case PlaceholderFillStrategy.GALLERY_IMAGES:
            req.addGalleryImagesPart(part.id, template);
            break;

          case PlaceholderFillStrategy.MOTIVE_STRUCTURE:
            req.addMotiveStructurePart(part.id, template);
            break;

          default:
            break;
        }
      }
      catch (e) {
        if (!(e instanceof MissingStructureError)) {
          throw e;
        }

        delete this.templatePreviews[template.id];
        continue;
      }

      this.templatePreviews[template.id] = {
        template: template,
        renderRequest: req,
        previewUrl: template.previewUrl,
      };
    }

    this.updateTemplatePreviews();
  }

  private updateTemplatePreviews(): void
  {
    for (const preview of Object.values(this.templatePreviews)) {
      this.updateTemplatePreview(preview);
    }
    this.changeDetector.detectChanges();
  }

  private updateTemplatePreview(preview: TemplatePreview): void
  {
    let renderRequest = preview.renderRequest;
    if (preview.renderRequest) {
      if (this.currentPreviewImageLongSide) {
        renderRequest.viewportLongSide = this.currentPreviewImageLongSide;
      }
      preview.previewUrl = renderRequest.url;
    } else {
      preview.previewUrl = preview.template.previewUrl;
    }
  }

  @ViewChild('templatePreviewImage') set templatePreviewImageElement(
    el: ElementRef | undefined
  )
  {
    if (!!this.previewImageResizeObserver) {
      this.previewImageResizeObserver.disconnect();
      this.previewImageResizeObserver = undefined;
    }

    if (!!el) {
      const obs = new ResizeObserver((_) => {
        this.previewImageLongside = Math.max(
          el.nativeElement.offsetWidth,
          el.nativeElement.offsetHeight
        );
      });

      obs.observe(el.nativeElement);
      this.previewImageResizeObserver = obs;
    }
  }

  private set previewImageLongside(size: number)
  {
    const alignedLongSide = this.prnglService.snapToViewportStep(size);

    if (alignedLongSide === this.currentPreviewImageLongSide) {
      return;
    }

    this.currentPreviewImageLongSide = alignedLongSide;
    this.updateTemplatePreviews();
  }
}

interface TemplatePreview {
  template: TemplateConfig;
  renderRequest?: RenderRequest;
  previewUrl: string;
}
